import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../css/shared/BetSlipSheet.css';
import { useLang } from '../../i18n';
import { useGeo } from '../../geo';

const API = process.env.REACT_APP_API_BASE_URL;

function Trash() {
  return (
    <svg viewBox="0 0 14 15" width="14" height="15" fill="none">
      <path d="M2 3.5h10M5.5 3V1.8h3V3M3 3.5l.6 9a1 1 0 001 .9h4.8a1 1 0 001-.9l.6-9M6 6v5M8 6v5"
        stroke="#757490" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}


function money(n) { return (Math.round(n * 100) / 100).toFixed(2); }

export default function BetSlipSheet({ open, onClose }) {
  const { t } = useLang();
  const { betChips, symbol, rate } = useGeo().prices;
  const [bets, setBets] = useState(() =>
    JSON.parse(localStorage.getItem('betSlipItems') || '[]')
  );
  const [user, setUser] = useState(() => {
    const u = localStorage.getItem('user');
    return u ? JSON.parse(u) : null;
  });
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [success, setSuccess] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const load = () => setBets(JSON.parse(localStorage.getItem('betSlipItems') || '[]'));
    window.addEventListener('betAdded', load);
    return () => window.removeEventListener('betAdded', load);
  }, []);

  useEffect(() => {
    localStorage.setItem('betSlipItems', JSON.stringify(bets));
  }, [bets]);

  const setStake = (id, val) => {
    setBets(prev => prev.map(b => b.id === id ? { ...b, stake: parseFloat(val) || 0 } : b));
  };

  const removeBet = (id) => {
    const updated = bets.filter(b => b.id !== id);
    setBets(updated);
    localStorage.setItem('betSlipItems', JSON.stringify(updated));
    window.dispatchEvent(new Event('betAdded'));
  };

  const clearAll = () => {
    setBets([]);
    localStorage.removeItem('betSlipItems');
    window.dispatchEvent(new Event('betAdded'));
  };

  const totalOdds = bets.reduce((p, b) => p * b.odds, 1);
  const totalStake = bets.reduce((s, b) => s + (b.stake || 0), 0);
  const potentialWin = totalStake * totalOdds;
  const canPlace = totalStake >= rate;

  const showToast = (type, msg) => { setToast({ type, msg }); setTimeout(() => setToast(null), 3500); };

  const placeBet = async () => {
    if (!user) { showToast('error', t('betslip.loginTobet')); setTimeout(() => navigate('/auth'), 1500); return; }
    if (!canPlace) { showToast('error', t('betslip.minimumStake', { min: `${rate.toFixed(2)} ${symbol}` })); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/bets/place.php`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.id, bets: bets.map(b => ({ ...b, stake: b.stake ? parseFloat((b.stake / rate).toFixed(2)) : 0 })) })
      });
      const data = await res.json();
      if (data.success) {
        const updated = { ...user, solde_actuel: data.data.nouveau_solde };
        localStorage.setItem('user', JSON.stringify(updated));
        setUser(updated);
        window.dispatchEvent(new Event('userUpdated'));
        setSuccess({ stake: totalStake, win: potentialWin, odds: totalOdds });
        setTimeout(() => { clearAll(); onClose(); setSuccess(null); }, 2200);
      } else { showToast('error', data.message || t('common.error')); }
    } catch { showToast('error', t('common.connectionError')); } finally { setLoading(false); }
  };

  if (!open) return null;

  return (
    <div className="bs-root">
      <div className="bs-backdrop" onClick={onClose} />
      <div className="bs-sheet">

        {success && (
          <div className="bs-success">
            <div className="bs-success-ring">
              <svg className="bs-success-check" viewBox="0 0 52 52" fill="none">
                <circle cx="26" cy="26" r="24" stroke="url(#bsg)" strokeWidth="2.5" />
                <path className="bs-check-path" d="M14 26l9 9 15-16" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                <defs>
                  <linearGradient id="bsg" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0" stopColor="#8187f6" />
                    <stop offset="1" stopColor="#76e1ff" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
            <div className="bs-success-title">{t('betslip.betPlaced')}</div>
            <div className="bs-success-row">
              <span className="bs-success-k">{t('mybets.stake')}</span>
              <span className="bs-success-v">{money(success.stake)} {symbol}</span>
            </div>
            <div className="bs-success-row">
              <span className="bs-success-k">{t('mybets.odds')}</span>
              <span className="bs-success-v">{money(success.odds)}</span>
            </div>
            <div className="bs-success-row win">
              <span className="bs-success-k">{t('mybets.potentialWin')}</span>
              <span className="bs-success-v">{money(success.win)} {symbol}</span>
            </div>
          </div>
        )}

        <div className="bs-head">
          <span className="bs-head-title">
            {t('betslip.title')}
            <span className="bs-head-count">{bets.length}</span>
          </span>
          {bets.length > 0 && (
            <button className="bs-clear-all" onClick={clearAll}>{t('betslip.clearAll')}</button>
          )}
        </div>

        <div className="bs-body">
          {bets.length === 0 ? (
            <div className="bs-empty">
              <svg viewBox="0 0 24 24" width="40" height="40" fill="none">
                <path d="M5 3.2h11a1 1 0 011 1V20l-2-1.3L13 20l-2-1.3L9 20l-2-1.3L5 20V4.2a1 1 0 010-1z" fill="rgba(255,255,255,.06)" />
                <path d="M7.5 7.5h7M7.5 10.5h7M7.5 13.5h4" stroke="#3a3857" strokeWidth="1.1" strokeLinecap="round" />
              </svg>
              <p>{t('betslip.empty')}</p>
              <span>{t('betslip.emptySub')}</span>
            </div>
          ) : (
            bets.map(b => (
              <div className="bs-sel" key={b.id}>
                <div className="bs-sel-top">
                  <div>
                    <div className="bs-sel-pick">{b.betLabel || b.pick || b.selection}</div>
                    <div className="bs-sel-match">{b.match}</div>
                    {(b.betType || b.market) && <div className="bs-sel-market">{b.betType || b.market}</div>}
                  </div>
                  <div className="bs-sel-right">
                    <span className="bs-sel-odds">{b.odds.toFixed(2)}</span>
                    <button onClick={() => removeBet(b.id)}><Trash /></button>
                  </div>
                </div>
                <div className="bs-stake">
                  <input
                    type="number"
                    className="bs-stake-input"
                    placeholder={t('betslip.stakePlaceholder', { symbol })}
                    value={b.stake || ''}
                    min="0"
                    step="1"
                    onChange={e => setStake(b.id, e.target.value)}
                  />
                </div>
                <div className="bs-chips">
                  {betChips.map(v => (
                    <button key={v} className="bs-chip" onClick={() => setStake(b.id, v)}>{v}{symbol}</button>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>

        {bets.length > 0 && (
          <>
            <div className="bs-summary">
              <div className="bs-row">
                <span className="k">{t('betslip.numberOfBets')}</span>
                <span className="v">{bets.length}</span>
              </div>
              <div className="bs-row">
                <span className="k">{t('betslip.totalOdds')}</span>
                <span className="v">{money(totalOdds)}</span>
              </div>
              <div className="bs-row">
                <span className="k">{t('betslip.totalStake')}</span>
                <span className="v">{money(totalStake)} <span className="cur">{symbol}</span></span>
              </div>
              <div className="bs-row">
                <span className="k strong">{t('betslip.potentialWin')}</span>
                <span className="v win">{money(potentialWin)} <span className="cur">{symbol}</span></span>
              </div>
            </div>

            <button className="bs-place" disabled={!canPlace || loading} onClick={placeBet}>
              {loading ? t('betslip.processing') : t('betslip.placeBet')}
            </button>

          </>
        )}
      </div>

      {toast && <div className={'bs-toast bs-toast--' + toast.type}>{toast.msg}</div>}
    </div>
  );
}

export function BetSlipFab({ count, onClick }) {
  return (
    <button className="bs-fab" onClick={onClick} aria-label="Betslip">
      <svg viewBox="0 0 24 24" width="24" height="24" fill="none">
        <path d="M5 3.2h11a1 1 0 011 1V20l-2-1.3L13 20l-2-1.3L9 20l-2-1.3L5 20V4.2a1 1 0 010-1z" fill="#fff" />
        <path d="M7.5 7.5h7M7.5 10.5h7M7.5 13.5h4" stroke="#8187f6" strokeWidth="1.1" strokeLinecap="round" />
      </svg>
      {count > 0 && <span className="bs-count">{count}</span>}
    </button>
  );
}
